import React, { useState } from 'react';
import { useGenerateJobDescription, useGenerateJobBenefits, useGenerateJobRequirements, useSuggestSkills } from '../../hooks/unified/useAIQueries';

type FieldType = 'description' | 'requirements' | 'benefits' | 'skills';

interface AIFieldGeneratorProps {
  fieldType: FieldType;
  jobTitle: string;
  companyId?: string;
  companyName?: string;
  companyInfo?: string;
  experienceLevel?: string;
  industry?: string;
  location?: string;
  onGenerated: (content: string | string[]) => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
}

export const AIFieldGenerator: React.FC<AIFieldGeneratorProps> = ({
  fieldType,
  jobTitle,
  companyId,
  companyName,
  companyInfo,
  experienceLevel,
  industry,
  location,
  onGenerated,
  disabled = false,
  size = 'sm',
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateDescription = useGenerateJobDescription();
  const generateBenefits = useGenerateJobBenefits();
  const generateRequirements = useGenerateJobRequirements();
  const suggestSkills = useSuggestSkills();

  const handleGenerate = async () => {
    if (!jobTitle.trim()) {
      setError('Please provide a job title first');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      let result: string | string[] = '';

      switch (fieldType) {
        case 'description':
          const descResponse = await generateDescription.mutateAsync({
            job_title: jobTitle,
            experience_level: experienceLevel,
            company_name: companyName,
            industry,
            location,
          } as any);
          result = typeof descResponse.content === 'string' 
            ? descResponse.content 
            : JSON.stringify(descResponse.content);
          break;

        case 'benefits':
          const benefitsResponse = await generateBenefits.mutateAsync({
            job_title: jobTitle,
            level: experienceLevel,
            company_id: companyId,
            company_name: companyName,
            company_info: companyInfo,
          });
          result = Array.isArray(benefitsResponse.content) 
            ? benefitsResponse.content 
            : typeof benefitsResponse.content === 'string'
              ? tryParseArray(benefitsResponse.content)
              : [];
          break;

        case 'requirements':
          const reqResponse = await generateRequirements.mutateAsync({
            job_title: jobTitle,
            seniority_level: experienceLevel,
          });
          // Extract required array from response
          const reqContent = reqResponse.content;
          if (typeof reqContent === 'object' && reqContent !== null && 'required' in reqContent) {
            result = (reqContent as any).required || [];
          } else if (Array.isArray(reqContent)) {
            result = reqContent;
          } else if (typeof reqContent === 'string') {
            result = tryParseArray(reqContent);
          } else {
            result = [];
          }
          break;

        case 'skills':
          const skillsResponse = await suggestSkills.mutateAsync({
            job_title: jobTitle,
          });
          result = Array.isArray(skillsResponse.content) 
            ? skillsResponse.content 
            : typeof skillsResponse.content === 'string'
              ? tryParseArray(skillsResponse.content)
              : [];
          break;
      }

      onGenerated(result);
    } catch (err: any) {
      setError(err.message || `Failed to generate ${fieldType}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const buttonClasses = size === 'sm'
    ? 'px-2 py-1 text-xs'
    : 'px-3 py-1.5 text-sm';

  return (
    <div className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={handleGenerate}
        disabled={disabled || isGenerating || !jobTitle}
        className={`${buttonClasses} font-medium rounded-md text-white bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1`}
        title={`Generate ${fieldType} with AI`}
      >
        {isGenerating ? (
          <>
            <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <span>AI...</span>
          </>
        ) : (
          <>
            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span>AI</span>
          </>
        )}
      </button>
      {error && <span className="text-xs text-red-500">{error}</span>}
    </div>
  );
};

// Helper to parse JSON array from string
function tryParseArray(str: string): string[] {
  try {
    const parsed = JSON.parse(str);
    return Array.isArray(parsed) ? parsed : [str];
  } catch {
    // Try to split by newlines or commas
    return str.split(/[\n,]/).map(s => s.trim()).filter(Boolean);
  }
}

export default AIFieldGenerator;
